import {
  type Breakpoint,
  Dialog,
  DialogActions,
  DialogContent,
  type DialogContentProps,
  type DialogProps,
  DialogTitle,
} from "@mui/material";
import { CancelButton } from "core/components/CancelButton";
import { ErrorComponent } from "core/components/ErrorComponent";
import { Loading } from "core/components/Loading";
import { ResetButton } from "core/components/ResetButton";
import { SubmitButton } from "core/components/SubmitButton";
import type { APIError } from "core/entities";
import type { FormEventHandler, ReactNode } from "react";

const DisabledButtons = (
  <>
    <CancelButton disabled />
    <ResetButton disabled />
    <SubmitButton disabled />
  </>
);

type DialogFormProps = {
  title: string;
  children: ReactNode;
  isLoading?: boolean;
  isError?: APIError;
  isSubmitting?: boolean;
  open: boolean;
  onDialogClose: DialogProps["onClose"];
  onSubmit: FormEventHandler<HTMLFormElement>;
  onCancelButtonClick: () => void;
  onResetButtonClick?: () => void;
  loadingSlot?: ReactNode;
  errorSlot?: ReactNode;
  maxWidth?: Breakpoint | false;
  scroll?: DialogProps["scroll"];
  isSubmitDisabled?: boolean;
  actionSlot?: ReactNode;
  dialogContentProps?: DialogContentProps;
  disableActions?: boolean;
  hideActions?: boolean;
  keepMounted?: boolean;
};

export function DialogFormContainer({
  title,
  children,
  open,
  onDialogClose,
  onSubmit,
  onCancelButtonClick,
  onResetButtonClick,
  isLoading,
  isError,
  isSubmitting,
  loadingSlot,
  errorSlot,
  scroll,
  maxWidth = "md",
  isSubmitDisabled = false,
  actionSlot,
  dialogContentProps,
  disableActions = false,
  hideActions = false,
  keepMounted = false,
}: DialogFormProps) {
  const Title = (
    <DialogTitle variant="h5" textAlign="center">
      {title}
    </DialogTitle>
  );
  if (isLoading) {
    return (
      <Dialog
        keepMounted={keepMounted}
        open={open}
        onClose={onDialogClose}
        maxWidth={maxWidth}
        fullWidth
        scroll={scroll}
        slotProps={{ paper: { sx: { backgroundColor: "background.default" } } }}
      >
        {Title}
        <DialogContent>{loadingSlot ?? <Loading />}</DialogContent>
        <DialogActions>{DisabledButtons}</DialogActions>
      </Dialog>
    );
  }
  if (isError) {
    return (
      <Dialog
        keepMounted={keepMounted}
        open={open}
        onClose={onDialogClose}
        maxWidth={maxWidth}
        fullWidth
        scroll={scroll}
        slotProps={{ paper: { sx: { backgroundColor: "background.default" } } }}
      >
        {Title}
        <DialogContent>
          {errorSlot ?? <ErrorComponent apiError={isError} />}
        </DialogContent>
        <DialogActions>{DisabledButtons}</DialogActions>
      </Dialog>
    );
  }
  return (
    <Dialog
      open={open}
      onClose={onDialogClose}
      maxWidth={maxWidth}
      fullWidth
      scroll={scroll}
      keepMounted={keepMounted}
      slotProps={{
        paper: {
          sx: { backgroundColor: "background.default" },
          component: "form",
          onSubmit,
        },
      }}
    >
      {Title}
      <DialogContent {...dialogContentProps}>{children}</DialogContent>
      {!hideActions && (
        <DialogActions sx={{ flexWrap: "wrap" }}>
          {disableActions ? (
            DisabledButtons
          ) : (
            <>
              {actionSlot}
              <CancelButton
                onClick={onCancelButtonClick}
                disabled={isSubmitting}
              />
              {onResetButtonClick && (
                <ResetButton
                  onClick={onResetButtonClick}
                  disabled={isSubmitting}
                />
              )}
              <SubmitButton
                loading={isSubmitting}
                disabled={isSubmitDisabled}
              />
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
